import { Component, OnInit } from '@angular/core';
import { SentimentAnalysisService } from '@services/sentiment-analysis.service';

interface FeedbackEntry {
  documentId: string;
  answerText: string;
  question: string;
  clientId: string;
  surveyId: string;
  postId: string;
  commentCreated: string;
  feedback: {
    userId: string;
    userEmail: string;
    feature: string;
    reaction: 'ThumbsUp' | 'ThumbsDown';
    comment: string;
    feedbackAt: string;
  };
  py_legal_analysis?: any;
}

interface FeedbackSummary {
  totalDocuments: number;
  totalFeedbackEntries: number;
  thumbsUp: number;
  thumbsDown: number;
  byFeature: {
    [feature: string]: {
      thumbsUp: number;
      thumbsDown: number;
      total: number;
    };
  };
}

@Component({
    selector: 'app-ai-feedback',
    templateUrl: './ai-feedback.component.html',
    styleUrls: ['./ai-feedback.component.scss'],
    standalone: false
})
export class AiFeedbackComponent implements OnInit {
  // Filter options
  selectedFeature = '';
  selectedReaction = '';
  featureOptions: string[] = ['legal', 'sentiment', 'classification', 'overall'];
  reactionOptions: string[] = ['ThumbsUp', 'ThumbsDown'];

  // Data
  feedbackList: FeedbackEntry[] = [];
  summary: FeedbackSummary | null = null;
  isLoading = false;
  errorMessage = '';

  // Pagination
  pageSize = 50;
  currentPage = 1;
  totalRecords = 0;

  // Expanded rows
  expandedRows: { [key: string]: boolean } = {};

  constructor(
    private sentimentAnalysisService: SentimentAnalysisService
  ) {}

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  ngOnInit(): void {
    this.loadFeedback();
  }

  loadFeedback(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const options: any = {
      size: this.pageSize,
      from: (this.currentPage - 1) * this.pageSize
    };

    if (this.selectedFeature) {
      options.feature = this.selectedFeature;
    }
    if (this.selectedReaction) {
      options.reaction = this.selectedReaction;
    }

    this.sentimentAnalysisService.listAIFeedback(options).subscribe({
      next: (response: any) => {
        console.log('response', response);
        this.feedbackList = response.body.feedback || [];
        this.summary = response.body.summary || null;
        this.totalRecords = response.body.total || 0;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading AI feedback:', error);
        this.errorMessage = 'Failed to load AI feedback. Please try again.';
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadFeedback();
  }

  clearFilters(): void {
    this.selectedFeature = '';
    this.selectedReaction = '';
    this.currentPage = 1;
    this.loadFeedback();
  }

  nextPage(): void {
    if (this.currentPage * this.pageSize < this.totalRecords) {
      this.currentPage++;
      this.loadFeedback();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadFeedback();
    }
  }

  toggleRowExpanded(documentId: string): void {
    this.expandedRows[documentId] = !this.expandedRows[documentId];
  }

  isRowExpanded(documentId: string): boolean {
    return !!this.expandedRows[documentId];
  }

  getReactionIcon(reaction: string): string {
    return reaction === 'ThumbsUp' ? 'fa-thumbs-up' : 'fa-thumbs-down';
  }

  getReactionClass(reaction: string): string {
    return reaction === 'ThumbsUp' ? 'reaction-positive' : 'reaction-negative';
  }

  truncateText(text: string, maxLength: number = 100): string {
    if (!text) {return '';}
    if (text.length <= maxLength) {return text;}
    return text.substring(0, maxLength) + '...';
  }

  getFeatureLabel(feature: string): string {
    const labels: { [key: string]: string } = {
      legal: 'Legal Analysis',
      sentiment: 'Sentiment Analysis',
      classification: 'Classification',
      overall: 'Overall Impressions'
    };
    return labels[feature] || feature;
  }

  getUrgencyClass(urgency: string): string {
    if (!urgency) {return '';}
    switch (urgency.toLowerCase()) {
      case 'routine':
        return 'urgency-routine';
      case 'medium':
      case '48hr':
        return 'urgency-medium';
      case 'high':
      case '24hr':
        return 'urgency-high';
      case 'critical':
      case 'immediate':
        return 'urgency-critical';
      default:
        return '';
    }
  }
}
